import { Injectable } from '@angular/core';

export interface IListTableColumnWidths {
  columns: string[];
}

/**
 * Used as setting property keys, must match legacy value to migrate properly. 
 */
export const settingsKey = {
  LastViewedDashboardId: "last-viewed-dashboard-id",
  SidebarCollapsedState: "client-dashboard-sidebar-collapsed-state",
  ListTableHeaderWidths: "list-table-header-widths",
} as const;

const legacyListTableHeaderWidthsPrefix = `${settingsKey.ListTableHeaderWidths}:`;

export interface IDashboardSettings {
  [settingsKey.LastViewedDashboardId]?: number;
  [settingsKey.SidebarCollapsedState]?: boolean;
  [settingsKey.ListTableHeaderWidths]?: Record<string, IListTableColumnWidths>;
}

/**
 * Consolidates localStorage entries into a single "dashboard-settings". 
 * Any legacy entries are migrated and removed.
 */
@Injectable({
  providedIn: 'root'
})
export class DashboardLocalStorageService {
  public static readonly StorageKey = "dashboard-settings";

  private Settings: IDashboardSettings;

  public constructor() {
    this.Settings = this.LoadSettings();
    this.SaveSettings();
  }

  private LoadSettings(): IDashboardSettings {
    const raw = localStorage.getItem(DashboardLocalStorageService.StorageKey);
    if (raw === null) {
      return {};
    }

    try {
      return JSON.parse(raw) ?? {};
    } catch {
      return {};
    }
  }

  private SaveSettings(): void {
    this.MigrateLegacyLocalStorage();
    localStorage.setItem(DashboardLocalStorageService.StorageKey, JSON.stringify(this.Settings));
  }

  private MigrateLegacyLocalStorage(): void {
    const legacyLastViewedDashboardId = localStorage.getItem(settingsKey.LastViewedDashboardId);
    if (legacyLastViewedDashboardId !== null) {
      const idInt = parseInt(legacyLastViewedDashboardId);
      if (!isNaN(idInt)) {
        this.Settings[settingsKey.LastViewedDashboardId] = idInt;
      }
      localStorage.removeItem(settingsKey.LastViewedDashboardId);
    }

    const legacySidebarCollapsedState = localStorage.getItem(settingsKey.SidebarCollapsedState);
    if (legacySidebarCollapsedState !== null) {
      this.Settings[settingsKey.SidebarCollapsedState] = legacySidebarCollapsedState === "true";
      localStorage.removeItem(settingsKey.SidebarCollapsedState);
    }

    const legacyListTableKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null && key.startsWith(legacyListTableHeaderWidthsPrefix)) {
        legacyListTableKeys.push(key);
      }
    }

    for (const key of legacyListTableKeys) {
      const tableId = key.substring(legacyListTableHeaderWidthsPrefix.length);
      const rawColumns = localStorage.getItem(key);
      if (rawColumns !== null) {
        try {
          const columns = JSON.parse(rawColumns);
          if (Array.isArray(columns)) {
            const listTableHeaderWidths = this.Settings[settingsKey.ListTableHeaderWidths] ??= {};
            listTableHeaderWidths[tableId] = { columns };
          }
        } catch {
          // Ignore malformed legacy entries
        }
      }
      localStorage.removeItem(key);
    }
  }

  public Get<SingleSettingKey extends keyof IDashboardSettings>(key: SingleSettingKey): NonNullable<IDashboardSettings[SingleSettingKey]> | null {
    return this.Settings[key] ?? null;
  }

  public Set<SingleSettingKey extends keyof IDashboardSettings>(key: SingleSettingKey, value: NonNullable<IDashboardSettings[SingleSettingKey]>): void {
    this.Settings[key] = value;
    this.SaveSettings();
  }

  public Remove<SingleSettingKey extends keyof IDashboardSettings>(key: SingleSettingKey): void {
    delete this.Settings[key];
    this.SaveSettings();
  }

  public GetTableColumnWidths(tableId: string): string[] | null {
    return this.Settings[settingsKey.ListTableHeaderWidths]?.[tableId]?.columns ?? null;
  }

  public SetTableColumnWidths(tableId: string, columns: string[]): void {
    const listTableHeaderWidths = this.Settings[settingsKey.ListTableHeaderWidths] ??= {};
    listTableHeaderWidths[tableId] = { columns };
    this.SaveSettings();
  }
}
